import { CurrencyType, WeightUnit } from '@uturn/api/v1';
import { z } from '@uturn/portal/zod';
import { containerNumberOrEmptySchema } from '../../../schema/container';

const itemSchema = z.object({
	value: z.string(),
	label: z.string(),
});

// Date rules (per-location ordering, sequential ordering across locations, and
// pickup-must-be-in-the-future) live in `validateLocationDates` instead of in this
// schema. They need (a) sibling-location data, (b) the current time, and (c) the
// user's publish intent — none of which are available to a Zod refinement on a
// single location. Manual create uses the same validator, so keeping the rules
// there guarantees parity.
const locationSchema = z.object({
	location: z.object({
		id: z.number().optional(),
		googlePlaceId: z.string().optional(),
	}),
	from: z.date(),
	until: z.date(),
	type: itemSchema, // Should be PICKUP, DROP, STOP, DELIVER etc. (enum preferably)
	reference: z.string().optional(),
	vesselName: z.string().optional(),
	port: z.string().optional(),
	remarks: z.string().optional(),
	shippingLine: itemSchema.optional(),
});

export const targetPriceSchema = z.object({
	price: z.object({ quantity: z.number() }),
});

export const schema = z.object({
	containerType: itemSchema.extend({ isoCode: z.string() }),
	isoCode: z.string().length(4),
	generatorSet: z.boolean().default(false),
	// Base rules only (length, alpha, digits, allows empty). The SOWN-aware
	// 4th-char rule runs imperatively in bulk.tsx so it fires even when the
	// rest of the row's required fields are still empty (matches manual).
	containerNumber: containerNumberOrEmptySchema.optional(),
	shippingLine: itemSchema
		.extend({ scacCode: z.string().optional() })
		.optional(),
	seal: z.string().optional(),
	cargoDescription: z.string().optional(),
	requirements: z.number().array().optional(),
	unCode: z.string().length(4).pipe(z.coerce.number()).optional(),
	cargoGrossWeight: z.object({
		quantity: z.number().max(99999),
		unit: z.nativeEnum(WeightUnit),
	}),
	price: z.object({
		quantity: z.number(),
		currency: z.nativeEnum(CurrencyType),
	}),
	acceptancePrice: z.boolean().default(false),
	invoiceReference: z.string().optional(),
	vat: z.boolean().default(false),
	preferredCarriers: z.number().array().optional(),
	termsAndConditions: itemSchema,
	locations: locationSchema.array(),
});

type Create = z.infer<typeof schema>;
type PartialCreate = Partial<Create>;

type Transform<T> = {
	[K in keyof T]: { value: T[K]; isValid?: boolean; errorMessage?: string };
};
export type TransformedPartialCreate = Transform<PartialCreate>;

export const transformBack = <T>(obj: Transform<T>) =>
	Object.keys(obj).reduce((acc, key) => {
		acc[key as keyof T] = obj[key as keyof Transform<T>].value;
		return acc;
	}, {} as T);
